import { useCallback, useEffect, useState } from 'react';
import { useWeb3React } from '@web3-react/core';
import Button from 'react-bootstrap/Button';
import Spinner from 'react-bootstrap/Spinner';
import Countdown from 'react-countdown';
import ReactMarkdown from 'react-markdown';

import useRainbows from '../hooks/useRainbows';
import useAuctionHouse from '../hooks/useAuctionHouse';
import useRainbowBalance from '../hooks/useRainbowBalance';
import { formatEther, ZERO, estimateGasLimit } from '../util';
import RainbowBalance from './RainbowBalance';

const AuctionEntryForm = ({ auctionId, purchaseLimit, winnerInstructions }) => {
  const { account } = useWeb3React();
  const rainbows = useRainbows();
  const auctionHouse = useAuctionHouse();
  const rainbowBalance = useRainbowBalance();

  const [ready, setReady] = useState(false);
  const [auction, setAuction] = useState();
  const [auctionStarted, setAuctionStarted] = useState(false);
  const [auctionEnded, setAuctionEnded] = useState(false);

  const [bids, setBids] = useState({});
  const [bidding, setBidding] = useState(false);
  const [currentPrice, setCurrentPrice] = useState(ZERO);
  const [nextPrice, setNextPrice] = useState(ZERO);
  const [nextPriceChange, setNextPriceChange] = useState();
  const [purchases, setPurchases] = useState(0);

  useEffect(() => {
    let ignore = false;
    setReady(false);
    setAuction(null);

    if (auctionHouse) {
      const fetchAuction = () => {
        auctionHouse.auctions(auctionId).then((auction) => {
          const [
            _,
            startingPrice,
            startingTime,
            quantity,
            depreciationFactor,
            depreciationInterval,
            bids,
          ] = auction;

          if (!ignore) {
            setAuction({
              startingPrice,
              startingTime: new Date(startingTime * 1000),
              quantity,
              depreciationFactor,
              depreciationInterval,
              bids,
            });

            setReady(true);
          }
        });
      };

      fetchAuction();
      const bid = auctionHouse.filters.SuccessfulBid(auctionId);
      auctionHouse.on(bid, fetchAuction);
      return () => {
        auctionHouse.off(bid, fetchAuction);
        ignore = true;
      };
    }
  }, [auctionId, auctionHouse]);

  useEffect(() => {
    setAuctionStarted(false);
    setAuctionEnded(false);
    if (auction) {
      setAuctionStarted(auction.startingTime.getTime() < Date.now());
      setAuctionEnded(auction.bids >= auction.quantity);
    }
  }, [auction]);

  useEffect(() => {
    let ignore = false;

    setBids({});
    const processBid = (event) => {
      const { transactionHash } = event;
      if (!ignore) {
        setBids((bids) => ({
          ...bids,
          [transactionHash]: event.args,
        }));
      }
    };

    const bid = auctionHouse.filters.SuccessfulBid(auctionId);
    auctionHouse.queryFilter(bid).then((bids) => bids.forEach(processBid));
    auctionHouse.on(bid, (_auctionId, _sender, _price, event) =>
      processBid(event)
    );
    return () => {
      auctionHouse.removeAllListeners(bid);

      ignore = true;
    };
  }, [auctionId, auctionHouse]);

  useEffect(() => {
    const purchases =
      account &&
      Object.values(bids).filter((bid) => bid.sender === account).length;
    setPurchases(purchases);
  }, [bids, account]);

  useEffect(() => {
    let ignore = false;

    const fetchPrice = () => {
      if (auctionHouse) {
        const now = Math.floor(Date.now() / 1000);
        auctionHouse.priceAtTime(auctionId, now).then((currentPrice) => {
          if (!ignore) {
            setCurrentPrice(currentPrice);
          }
        });
        auctionHouse.nextPriceChange(auctionId, now).then((timestamp) => {
          auctionHouse.priceAtTime(auctionId, timestamp).then((price) => {
            if (!ignore) {
              const nextPriceChange = new Date(timestamp.toNumber() * 1000);
              setNextPrice(price);
              setNextPriceChange(nextPriceChange);
            }
          });
        });
      }
    };

    fetchPrice();

    return () => {
      ignore = true;
    };
  }, [auctionHouse, auctionId]);

  const updatePrice = useCallback(
    (nextPrice, nextPriceChange) => {
      setCurrentPrice(nextPrice);
      auctionHouse
        .nextPriceChange(
          auctionId,
          Math.floor(nextPriceChange.getTime() / 1000)
        )
        .then((timestamp) => {
          auctionHouse.priceAtTime(auctionId, timestamp).then((price) => {
            const nextPriceChange = new Date(timestamp.toNumber() * 1000);
            setNextPrice(price);
            setNextPriceChange(nextPriceChange);
          });
        });
    },
    [auctionHouse, auctionId]
  );

  const submitBid = useCallback(
    async (price) => {
      if (auctionHouse && rainbows) {
        const allowance = await rainbows.allowance(
          account,
          auctionHouse.address
        );

        if (allowance.lt(price)) {
          await rainbows
            .approve(auctionHouse.address, price)
            .then((tx) => {
              setBidding(true);
              return tx.wait();
            })
            .catch((e) => console.error(e))
            .finally(() => setBidding(false));
        }

        const gasLimit = await estimateGasLimit(
          auctionHouse,
          'submitBid',
          auctionId
        );

        await auctionHouse
          .submitBid(auctionId, { gasLimit })
          .then((tx) => {
            setBidding(true);
            return tx.wait();
          })
          .catch((e) => console.error(e))
          .finally(() => setBidding(false));
      }
    },
    [auctionId, auctionHouse, rainbows, account]
  );

  const disableBid =
    bidding || auctionEnded || rainbowBalance.current.lt(currentPrice);

  if (!ready) {
    return (
      <div className="py-5 text-center">
        <Spinner />
      </div>
    );
  }

  if (!auctionStarted) {
    return (
      <div className="py-5 text-center">
        <p className="lead">
          Auction will start in{' '}
          <strong>
            <Countdown
              date={auction.startingTime}
              days
              onComplete={() => setAuctionStarted(true)}
            />
          </strong>
        </p>
      </div>
    );
  }

  const fixedPrice = auction.depreciationFactor === 0;

  return (
    <>
      {purchases >= purchaseLimit ? (
        ''
      ) : auctionEnded ? (
        <>
          <div className="alert alert-dark text-center lead">
            This auction has ended
          </div>
          <div className="fs-3 fw-bold">Auction won by</div>
          {Object.entries(bids).map(([tx, bid]) => (
            <p key={tx} className="text-truncate">
              <strong>{bid.sender}</strong>
            </p>
          ))}
        </>
      ) : (
        <>
          <p className="lead fw-bold">
            Current price:
            <br />
            <span className="fs-2">{formatEther(currentPrice)}</span> $RAINBOWS
          </p>
          {fixedPrice ? (
            ''
          ) : (
            <div>
              {nextPrice && nextPriceChange ? (
                <p className="text-muted">
                  Price change in{' '}
                  <Countdown
                    key={nextPriceChange.getTime()}
                    date={nextPriceChange}
                    days
                    onComplete={() => updatePrice(nextPrice, nextPriceChange)}
                  />
                </p>
              ) : (
                <Spinner />
              )}
            </div>
          )}

          <Button
            onClick={() => submitBid(currentPrice)}
            disabled={disableBid}
            size="lg"
            className="btn-primary rounded-pill w-100"
          >
            {bidding ? (
              <>
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  role="status"
                  aria-hidden="true"
                />{' '}
              </>
            ) : (
              ''
            )}
            Buy Now
          </Button>
          <div className="mt-4">
            <RainbowBalance price={currentPrice} />
          </div>
          <hr />
          <p>
            <strong>Remaining items:</strong> {auction.quantity - auction.bids}
          </p>
        </>
      )}

      {purchases > 0 ? (
        <>
          <div className="alert alert-success text-center lead">
            <p>
              <span className="fw-bold">
                You won! {purchases > 1 ? `(x${purchases})` : ''}
              </span>
              <br />
              <small>{account}</small>
            </p>
            <ReactMarkdown
              components={{
                a: ({ node, ...props }) => (
                  <a
                    target="_blank"
                    rel="noreferrer"
                    className="link-success"
                    {...props}
                  />
                ),
              }}
            >
              {winnerInstructions}
            </ReactMarkdown>
          </div>
        </>
      ) : (
        ''
      )}
    </>
  );
};

AuctionEntryForm.defaultProps = {
  purchaseLimit: 1,
  winnerInstructions:
    'Please [create a ticket](https://discord.com/channels/929438050898026516/940792025094168616) in Discord to claim your prize.',
};

export default AuctionEntryForm;
